import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft, Car, FileText, Euro, Send, User, Phone, Mail, Truck, Ban,
  CheckCircle2, PackageCheck, Rocket, Calendar, Package, Lock, Unlock, Hand, Store, MapPin, Trash2, Wrench, Tag, X,
  CreditCard, HandCoins, Loader2,
} from "lucide-react";
import { api, formatApiError, STATUS_META, STAGES, normalizeStatus, hasPerm } from "@/lib/api";
import { Stepper } from "@/components/Stepper";
import MarginPanel from "@/components/MarginPanel";
import QuoteBuilder from "@/components/QuoteBuilder";
import { PaymentPanel } from "@/components/PaymentPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";

const STAGE_DESC = Object.fromEntries(STAGES.map((s) => [s.key, s.desc]));

// Compact coloured "advance order" actions for staff (post-quote lifecycle)
const FLOW_ACTIONS = [
  { key: "en_preparation", label: "En préparation", Icon: Wrench,
    active: "bg-amber-500 text-white border-amber-500", idle: "border-amber-300 text-amber-700 hover:bg-amber-50" },
  { key: "prete", label: "Prête à récupérer", Icon: PackageCheck,
    active: "bg-emerald-600 text-white border-emerald-600", idle: "border-emerald-300 text-emerald-700 hover:bg-emerald-50" },
  { key: "recuperee", label: "Récupérée", Icon: CheckCircle2,
    active: "bg-slate-900 text-white border-slate-900", idle: "border-slate-300 text-slate-700 hover:bg-slate-50" },
  { key: "annulee", label: "Annuler", Icon: Ban,
    active: "bg-rose-600 text-white border-rose-600", idle: "border-rose-300 text-rose-600 hover:bg-rose-50" },
];

const FULFILLMENT = {
  pickup: { label: "Retrait en magasin", Icon: Store },
  delivery: { label: "Livraison", Icon: Truck },
};

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const m = STATUS_META[status] || STATUS_META.en_attente;
  return <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${m.color}`}>{t(`status.${normalizeStatus(status)}`)}</span>;
}

function fmtDate(d) {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return d; }
}

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isStaff = user && (user.role === "admin" || user.role === "agent");
  const [q, setQ] = useState(null);
  const [lines, setLines] = useState([]);
  const [message, setMessage] = useState("");
  const [fulfillment, setFulfillment] = useState({ method: "pickup", fee: "", eta: "" });
  const [paymentRequired, setPaymentRequired] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [verifyingPayment, setVerifyingPayment] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/quotes/${id}`); setQ(data); return data; }
    catch (err) { toast.error(t("detail.notFound")); navigate("/app"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Verify a returning Stripe payment (?session_id=...) then refresh the quote
  useEffect(() => {
    const sid = searchParams.get("session_id");
    if (!sid) return;
    let attempts = 0;
    setVerifyingPayment(true);
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success(t("pay.paymentConfirmed"));
          await load();
          setVerifyingPayment(false);
          searchParams.delete("session_id"); setSearchParams(searchParams, { replace: true });
          return;
        }
        if (data.status === "expired" || attempts >= 6) {
          toast.error(t("pay.paymentFailed"));
          setVerifyingPayment(false);
          searchParams.delete("session_id"); setSearchParams(searchParams, { replace: true });
          return;
        }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch {
        setVerifyingPayment(false);
      }
    };
    poll();
    // eslint-disable-next-line
  }, []);

  // Initialise quote lines from parts (or existing quote) for staff
  useEffect(() => {
    if (!q || !isStaff) return;
    const existing = q.quote?.lines || [];
    const base = (q.parts || []).map((p, i) => {
      const ex = existing[i];
      return {
        name: p.name, quantity: p.quantity || 1,
        price: ex ? String(ex.price) : "",
        available_date: ex?.available_date || "",
        in_stock: ex ? ex.in_stock : true,
        brand: ex?.brand || "",
        reference: ex?.reference || p.reference || "",
        supplier: ex?.supplier || "",
      };
    });
    if (base.length === 0 && existing.length === 0) {
      base.push({ name: q.parts_description || "Pièce", quantity: 1, price: "", available_date: "", in_stock: true, brand: "", reference: "", supplier: "" });
    }
    setLines(base);
    setMessage(q.quote?.message || "");
    const dr = q.delivery_request || {};
    const exf = q.quote?.fulfillment;
    setFulfillment(exf
      ? { method: exf.method || "pickup", fee: exf.fee ? String(exf.fee) : "", eta: exf.eta || "" }
      : { method: dr.method || "pickup", fee: "", eta: "" });
    setPaymentRequired(!!q.quote?.payment?.required);
    // eslint-disable-next-line
  }, [q?.id]);

  const setLine = (i, patch) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const partsTotal = lines.reduce((s, l) => s + (parseFloat(l.price) || 0) * (l.quantity || 1), 0);
  const deliveryFee = fulfillment.method === "delivery" ? (parseFloat(fulfillment.fee) || 0) : 0;
  const total = partsTotal + deliveryFee;

  const sendQuote = async (e) => {
    e.preventDefault();
    if (lines.some((l) => l.price === "" || isNaN(parseFloat(l.price)))) {
      toast.error("Indiquez le prix de chaque pièce."); return;
    }
    if (lines.some((l) => !(l.reference || "").trim() || !(l.supplier || "").trim())) {
      toast.error("Référence et fournisseur sont obligatoires pour chaque pièce."); return;
    }
    setSaving(true);
    try {
      const payloadLines = lines.map((l) => ({
        name: l.name, quantity: l.quantity, price: parseFloat(l.price),
        available_date: l.available_date || null, in_stock: !!l.in_stock,
        brand: l.brand || null, reference: l.reference || null, supplier: l.supplier || null,
      }));
      const { data } = await api.post(`/quotes/${id}/respond`, {
        lines: payloadLines, message,
        fulfillment: { method: fulfillment.method, fee: deliveryFee, eta: fulfillment.method === "delivery" ? (fulfillment.eta || null) : null },
        payment_required: paymentRequired,
      });
      setQ(data);
      toast.success("Devis envoyé au client");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const changeStatus = async (status, label) => {
    try { const { data } = await api.post(`/quotes/${id}/status`, { status }); setQ(data); toast.success(label || t("detail.statusUpdated")); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const claim = async () => {
    try { const { data } = await api.post(`/quotes/${id}/claim`); setQ(data); toast.success("Demande prise en charge"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const release = async () => {
    try { const { data } = await api.post(`/quotes/${id}/release`); setQ(data); toast.success("Demande libérée"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteQuote = async () => {
    try {
      await api.delete(`/quotes/${id}`);
      toast.success("Demande supprimée");
      navigate("/app/admin/demandes");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!q) return <div className="py-20 text-center text-slate-400">Chargement...</div>;

  const v = q.vehicle || {};
  const norm = normalizeStatus(q.status);
  const hasQuote = !!q.quote;
  const quoteLines = q.quote?.lines || [];
  const quoteTotal = q.quote?.total ?? q.quote?.price;
  const dr = q.delivery_request || {};
  const drMeta = FULFILLMENT[dr.method] || FULFILLMENT.pickup;
  const qf = q.quote?.fulfillment;
  const qfMeta = qf ? (FULFILLMENT[qf.method] || FULFILLMENT.pickup) : null;
  const isAdmin = user?.role === "admin";
  const assignedToMe = q.assigned_to && q.assigned_to === user?.id;
  const lockedByOther = isStaff && q.assigned_to && !assignedToMe && !isAdmin;
  const canEdit = isStaff && !lockedByOther;
  const canQuote = canEdit && hasPerm(user, "quoting");
  const canFulfill = canEdit && hasPerm(user, "fulfillment");
  const canClaim = isStaff && hasPerm(user, "requests");
  const showBuilder = canQuote && ["en_attente", "devis_envoye"].includes(norm);
  const canDelete = isAdmin && ["recuperee", "annulee"].includes(norm);

  return (
    <div>
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> {t("detail.back")}
      </button>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-bold tracking-tight">{v.brand || t("detail.vehicle")} {v.model || ""}</h1>
        <div className="flex items-center gap-2">
          <StatusBadge status={q.status} />
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid="delete-request-btn"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-1.5 text-sm font-medium text-rose-600 transition-colors hover:bg-rose-50">
                  <Trash2 className="h-4 w-4" /> Supprimer
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="delete-confirm-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Supprimer cette demande ?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Cette action est irréversible. La demande, son devis et ses notifications seront définitivement supprimés.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="delete-cancel-btn">Annuler</AlertDialogCancel>
                  <AlertDialogAction onClick={deleteQuote} data-testid="delete-confirm-btn" className="bg-rose-600 hover:bg-rose-700">
                    Supprimer définitivement
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
      <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-500">
        {q.ref && <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 font-mono text-xs font-semibold text-slate-700" data-testid="request-ref">N° {q.ref}</span>}
        <span>{t("detail.createdOn")} {new Date(q.created_at).toLocaleDateString(i18n.language, { day: "numeric", month: "long", year: "numeric" })}</span>
      </p>

      {verifyingPayment && (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-medium text-blue-800" data-testid="payment-verifying">
          <Loader2 className="h-4 w-4 animate-spin" /> {t("detail.payVerifying")}
        </div>
      )}

      {/* Quick coloured "advance order" bar (staff) */}
      {canFulfill && norm !== "en_attente" && (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm" data-testid="status-actions">
          <span className="mr-1 flex items-center gap-1.5 text-sm font-semibold text-slate-700"><Rocket className="h-4 w-4 text-blue-600" /> Faire avancer&nbsp;:</span>
          {FLOW_ACTIONS.map(({ key, label, Icon, active, idle }) => {
            const isCur = norm === key;
            return (
              <button key={key} onClick={() => changeStatus(key)} disabled={isCur} data-testid={`status-action-${key}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors disabled:cursor-default ${isCur ? active : idle}`}>
                <Icon className="h-3.5 w-3.5" /> {label}{isCur ? " ✓" : ""}
              </button>
            );
          })}
        </div>
      )}

      {/* Tracking card */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-6" data-testid="tracking-card">
        <div className="flex items-center gap-2 text-slate-800">
          <Truck className="h-5 w-5 text-blue-600" />
          <h2 className="font-display text-lg font-semibold">{t("detail.trackTitle")}</h2>
        </div>
        <div className="mt-6 overflow-x-auto"><Stepper status={q.status} /></div>
        {norm !== "annulee" && (
          <p className="mt-5 rounded-lg bg-blue-50 px-4 py-2.5 text-sm font-medium text-blue-800">
            {t(`stages.${norm}_d`)}
          </p>
        )}
      </div>

      {isStaff && showBuilder && (
        <QuoteBuilder
          lines={lines} setLine={setLine}
          fulfillment={fulfillment} setFulfillment={setFulfillment}
          message={message} setMessage={setMessage}
          onSubmit={sendQuote} saving={saving} hasQuote={hasQuote}
          dr={dr} partsTotal={partsTotal} deliveryFee={deliveryFee} total={total}
          paymentRequired={paymentRequired} setPaymentRequired={setPaymentRequired}
        />
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Parts */}
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2 text-slate-800"><FileText className="h-5 w-5 text-blue-600" /><h2 className="font-display text-lg font-semibold">{t("detail.parts")}</h2>
              {q.parts?.length > 0 && <span className="ml-auto rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700">{q.parts.length}</span>}
            </div>
            {q.parts?.length > 0 ? (
              <ul className="mt-4 space-y-2.5" data-testid="detail-parts-list">
                {q.parts.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 rounded-lg border border-slate-100 bg-slate-50/60 px-3.5 py-2.5">
                    <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-md bg-white text-xs font-bold text-blue-600 shadow-sm">{p.quantity}×</span>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-800">{p.name}</p>
                      {p.reference && (
                        <p className="mt-0.5 flex items-center gap-1 font-mono text-xs text-slate-600">
                          <span className="rounded bg-slate-900 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">Réf</span>{p.reference}
                        </p>
                      )}
                      {p.note && <p className="text-xs text-slate-500">{p.note}</p>}
                    </div>
                    {p.photo && (
                      <button type="button" onClick={() => setLightbox(p.photo)} data-testid={`detail-part-photo-${i}`}
                        className="shrink-0 overflow-hidden rounded-lg border border-slate-200 transition-transform hover:scale-105">
                        <img src={p.photo} alt={p.name} className="h-12 w-16 object-cover" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 whitespace-pre-wrap text-sm text-slate-700">{q.parts_description}</p>
            )}
          </div>

          {/* Vehicle */}
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2 text-slate-800"><Car className="h-5 w-5 text-blue-600" /><h2 className="font-display text-lg font-semibold">{t("detail.vehicle")}</h2></div>
            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
              {[["VIN", v.vin, true], [t("nr.fBrand"), v.brand], [t("nr.fModel"), v.model], [t("nr.fYear"), v.year], [t("nr.fPlate"), v.plate], [t("nr.fFuel"), v.fuel], [t("nr.fEngine"), v.engine], [t("nr.fColor"), v.color]]
                .filter(([, val]) => val).map(([label, val, mono]) => (
                <div key={label}>
                  <dt className="text-xs uppercase tracking-wide text-slate-400">{label}</dt>
                  <dd className={`mt-0.5 font-medium text-slate-800 ${mono ? "font-mono text-xs" : ""}`}>{val}</dd>
                </div>
              ))}
            </dl>
            {q.carte_grise_image && (
              <button type="button" onClick={() => setLightbox(q.carte_grise_image)} data-testid="carte-grise-preview"
                className="mt-5 block overflow-hidden rounded-lg border border-slate-200 transition-opacity hover:opacity-90">
                <img src={q.carte_grise_image} alt="Carte grise" className="max-h-72 object-contain" />
              </button>
            )}
          </div>

          {/* Delivery / pickup */}
          <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="delivery-info-card">
            <div className="flex items-center gap-2 text-slate-800"><Truck className="h-5 w-5 text-blue-600" /><h2 className="font-display text-lg font-semibold">{t("detail.deliveryMode")}</h2></div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">{t("detail.requestedBy")}</p>
                <p className="mt-1.5 flex items-center gap-2 font-medium text-slate-800"><drMeta.Icon className="h-4 w-4 text-slate-500" /> {t(`nr.${dr.method === "delivery" ? "delivery" : "pickup"}`)}</p>
                {dr.method === "delivery" && (dr.address || dr.city) && (
                  <p className="mt-1.5 flex items-start gap-1.5 text-xs text-slate-500"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {[dr.address, dr.city].filter(Boolean).join(", ")}</p>
                )}
              </div>
              <div className="sm:border-l sm:border-slate-100 sm:pl-4">
                <p className="text-xs uppercase tracking-wide text-slate-400">{t("detail.resolved")}</p>
                {qfMeta ? (
                  <p className="mt-1.5 flex items-center gap-2 font-medium text-blue-700" data-testid="fulfillment-resolved">
                    <qfMeta.Icon className="h-4 w-4" /> {t(`nr.${qf.method === "delivery" ? "delivery" : "pickup"}`)}{qf.fee > 0 ? ` · ${Number(qf.fee).toFixed(2)} €` : ""}
                  </p>
                ) : (
                  <p className="mt-1.5 text-xs text-slate-400">{t("detail.awaitingConf")}</p>
                )}
                {qf?.eta && qf.method === "delivery" && (
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500" data-testid="fulfillment-eta"><Calendar className="h-3.5 w-3.5" /> {t("detail.etaLabel")} {qf.eta}</p>
                )}
              </div>
            </div>
          </div>

          {/* Quote shown (detailed lines) */}
          {hasQuote && (
            <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-6" data-testid="quote-response">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-lg font-semibold text-blue-900">{t("detail.quoteProposed")}</h2>
                <span className="font-display text-2xl font-bold text-blue-700" data-testid="quote-total">{Number(quoteTotal).toFixed(2)} €</span>
              </div>
              {q.quote?.payment && (
                <div className="mt-2" data-testid="quote-payment-badge">
                  {q.quote.payment.status === "paid" ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> {t("pay.badgePaid")}</span>
                  ) : q.quote.payment.required ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700"><CreditCard className="h-3.5 w-3.5" /> {t("pay.badgeRequired")}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700"><HandCoins className="h-3.5 w-3.5" /> {t("pay.badgeCredit")}</span>
                  )}
                </div>
              )}
              {quoteLines.length > 0 ? (
                <div className="mt-4 divide-y divide-blue-100 rounded-lg border border-blue-100 bg-white">
                  {quoteLines.map((l, i) => (
                    <div key={i} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3" data-testid={`quote-line-${i}`}>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800">{l.quantity}× {l.name}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${l.in_stock ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                            <Package className="h-3 w-3" /> {l.in_stock ? t("detail.inStock") : t("detail.onOrder")}
                          </span>
                          {l.available_date && (
                            <span className="inline-flex items-center gap-1 text-slate-500"><Calendar className="h-3 w-3" /> {t("detail.availFrom")} {fmtDate(l.available_date)}</span>
                          )}
                          {l.brand && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 font-semibold text-blue-700" data-testid={`quote-line-brand-${i}`}>
                              <Tag className="h-3 w-3" /> {l.brand}
                            </span>
                          )}
                          {l.reference && (
                            <span className="inline-flex items-center gap-1 font-mono text-slate-600">
                              <span className="rounded bg-slate-900 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">Réf</span>{l.reference}
                            </span>
                          )}
                          {isStaff && l.supplier && (
                            <span className="inline-flex items-center gap-1 text-slate-500" data-testid={`quote-line-supplier-${i}`}><Truck className="h-3 w-3" /> {l.supplier}</span>
                          )}
                        </div>
                      </div>
                      <span className="ml-3 shrink-0 whitespace-nowrap font-semibold text-slate-900">{(l.price * l.quantity).toFixed(2)} €</span>
                    </div>
                  ))}
                </div>
              ) : null}
              {q.quote?.delivery_fee > 0 && (
                <div className="mt-3 flex items-center justify-between rounded-lg border border-blue-100 bg-white px-4 py-2.5 text-sm" data-testid="quote-delivery-fee">
                  <span className="flex items-center gap-1.5 text-slate-600"><Truck className="h-4 w-4 text-blue-600" /> {t("detail.deliveryFee")}</span>
                  <span className="font-semibold text-slate-900">{Number(q.quote.delivery_fee).toFixed(2)} €</span>
                </div>
              )}
              {q.quote.message && <p className="mt-4 whitespace-pre-wrap text-sm text-slate-700">{q.quote.message}</p>}
              <p className="mt-3 text-xs text-slate-500">{t("detail.establishedBy", { name: q.quote.by_name })}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {isStaff && (
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="flex items-center gap-2 text-slate-800"><User className="h-5 w-5 text-blue-600" /><h2 className="font-display text-base font-semibold">Client</h2></div>
              <div className="mt-3 space-y-2 text-sm text-slate-600">
                <p className="font-medium text-slate-800">{q.user_name}</p>
                <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /> {q.user_email}</p>
                {q.user_phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> {q.user_phone}</p>}
              </div>
            </div>
          )}

          {/* Assignment / lock */}
          {isStaff && (
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="assignment-card">
              <div className="flex items-center gap-2 text-slate-800">
                {q.assigned_to ? <Lock className="h-5 w-5 text-blue-600" /> : <Unlock className="h-5 w-5 text-slate-400" />}
                <h2 className="font-display text-base font-semibold">Prise en charge</h2>
              </div>
              {!q.assigned_to ? (
                <>
                  <p className="mt-2 text-sm text-slate-500">Cette demande est libre.</p>
                  {canClaim ? (
                    <Button onClick={claim} data-testid="claim-btn" className="mt-3 w-full bg-blue-600 hover:bg-blue-700">
                      <Hand className="mr-1.5 h-4 w-4" /> Prendre en charge
                    </Button>
                  ) : (
                    <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">Vous n'avez pas l'accès « Demandes » pour la prendre en charge.</p>
                  )}
                </>
              ) : (
                <>
                  <p className={`mt-2 text-sm ${assignedToMe ? "text-blue-700" : "text-amber-700"}`}>
                    {assignedToMe ? "Vous traitez cette demande." : `Prise en charge par ${q.assigned_to_name}.`}
                  </p>
                  {(assignedToMe || isAdmin) && canClaim && (
                    <Button onClick={release} variant="outline" data-testid="release-btn" className="mt-3 w-full border-slate-300">
                      <Unlock className="mr-1.5 h-4 w-4" /> Libérer
                    </Button>
                  )}
                  {lockedByOther && (
                    <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700">
                      Verrouillée par un collègue — seul un super-admin peut intervenir.
                    </p>
                  )}
                  {isAdmin && !assignedToMe && (
                    <Button onClick={claim} data-testid="reassign-btn" className="mt-2 w-full bg-slate-900 hover:bg-slate-800">
                      <Hand className="mr-1.5 h-4 w-4" /> Me l'attribuer
                    </Button>
                  )}
                </>
              )}
            </div>
          )}

          {isStaff ? (
            <>
              {canFulfill && norm === "devis_valide" && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6" data-testid="launch-card">
                  <div className="flex items-center gap-2 text-emerald-800"><CheckCircle2 className="h-5 w-5" /><h2 className="font-display text-base font-semibold">Devis validé par le client</h2></div>
                  <p className="mt-1 text-sm text-emerald-700">Vous pouvez lancer la préparation de la commande.</p>
                  <Button onClick={() => changeStatus("en_preparation", "Commande lancée 🚀")} data-testid="launch-order-btn" className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700">
                    <Rocket className="mr-1.5 h-4 w-4" /> Lancer la commande
                  </Button>
                </div>
              )}
            </>
          ) : (
            <>
            {hasQuote && <PaymentPanel quote={q} amount={quoteTotal} />}
            {hasQuote && <MarginPanel quote={q} onUpdate={setQ} />}
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-600">
              <p className="font-medium text-slate-800">{t("detail.whatNow")}</p>
              <p className="mt-2">{t(`stages.${norm}_d`)}</p>
              {q.status === "devis_envoye" && (
                <Button className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => changeStatus("devis_valide", t("detail.quoteValidated"))} data-testid="validate-quote-btn">
                  <CheckCircle2 className="mr-1.5 h-4 w-4" /> {t("detail.validateQuote")}
                </Button>
              )}
              {norm === "devis_valide" && (
                <p className="mt-4 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2.5 text-emerald-700"><CheckCircle2 className="h-4 w-4" /> {t("detail.validated")}</p>
              )}
              {norm === "prete" && (
                <p className="mt-4 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2.5 font-medium text-emerald-700"><PackageCheck className="h-4 w-4" /> {t("detail.readyMsg")}</p>
              )}
              <a href="tel:024497317" className="mt-3 flex items-center justify-center gap-2 rounded-lg border border-slate-200 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                <Phone className="h-4 w-4" /> 02 449 73 17
              </a>
            </div>
            </>
          )}
        </div>
      </div>

      {lightbox && (
        <div onClick={() => setLightbox(null)} data-testid="image-lightbox"
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm">
          <button onClick={() => setLightbox(null)} data-testid="lightbox-close-btn"
            className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full bg-white/15 text-white hover:bg-white/25">
            <X className="h-6 w-6" />
          </button>
          <img src={lightbox} alt="Aperçu" onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-full rounded-lg object-contain shadow-2xl" />
        </div>
      )}
    </div>
  );
}
